import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import Chip from "@mui/material/Chip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GroupIcon from "@mui/icons-material/Group";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";
import { useSelector } from "react-redux";
import { Colors } from "../Colors";
import { useSnackbar } from "../providers/SnackbarProvider";
import { RootState } from "../store";
import { AutoComplete, AutoCompleteCheckbox } from "../subcomponents/AutoComplete";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { FrontendUser, Organization, User } from "../types";

const cardSx = {
  backgroundColor: "rgba(255, 255, 255, 0.02)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "12px",
};

const containedButtonSx = {
  textTransform: "none" as const,
  fontSize: "0.875rem",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const tableContainerSx = {
  backgroundColor: "rgba(255, 255, 255, 0.02)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "12px",
  boxShadow: "none",
};

const tableHeadRowSx = {
  "& th": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    color: Colors.info,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    py: 2,
  },
};

const tableBodyRowSx = {
  "& td": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
    py: 1.5,
  },
  "&:last-of-type td": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.02)",
  },
  transition: "background-color 0.2s ease",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    borderRadius: "8px",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.08)" },
    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
    "&.Mui-focused fieldset": { borderColor: Colors.primary },
  },
  "& .MuiInputLabel-root": { color: Colors.info },
  "& .MuiOutlinedInput-input": { color: "#FFF" },
  "& .MuiSelect-icon": { color: Colors.info },
};

function generateRandomPassword(): string {
  const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const symbols = "!@#$%^&*()-_=+[]{}|;:,.<>?/~";

  const allChars = uppercaseChars + lowercaseChars + numbers + symbols;
  const passwordLength = 12;
  let password = "";

  password += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
  password += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];

  for (let i = password.length; i < passwordLength; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  return password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}

interface UserRowProps {
  token: string;
  rowUser: User;
  organizations: Organization[];
  userIsAdmin: boolean;
  rowUserIsAdmin: boolean;
  onDisable: (user: User) => void;
}

const UserRow: React.FC<UserRowProps> = ({ token, rowUser, organizations, userIsAdmin, rowUserIsAdmin, onDisable }) => {
  const [selectedOrganizationIds, setSelectedOrganizationIds] = React.useState<string[]>(rowUser.orgIds || []);

  const orgOptions = React.useMemo(() => organizations.map((org) => ({ label: org.name, value: org.organizationId })), [organizations]);
  const selectedOrgOptions = React.useMemo(() => orgOptions.filter((o) => selectedOrganizationIds.includes(o.value)), [orgOptions, selectedOrganizationIds]);

  function handleOrganizationChange(next: { label: string; value: string }[]) {
    const selectedOptions = next.map((o) => o.value);
    setSelectedOrganizationIds(selectedOptions);
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/users/${rowUser.userId}`, { orgIds: selectedOptions }, { headers: { Authorization: `Bearer ${token}` } });
  }

  return (
    <TableRow sx={tableBodyRowSx}>
      <TableCell sx={{ pl: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: rowUserIsAdmin ? "rgba(252, 213, 84, 0.12)" : "rgba(94, 120, 238, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {rowUserIsAdmin ? <ManageAccountsOutlinedIcon sx={{ color: Colors.warning, fontSize: "18px" }} /> : <PersonOutlineIcon sx={{ color: Colors.primary, fontSize: "18px" }} />}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#FFF" }}>
              {rowUser.firstName} {rowUser.lastName}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: Colors.info, mt: 0.25 }}>{rowUser.email}</Typography>
          </Box>
        </Box>
      </TableCell>

      <TableCell>
        {rowUserIsAdmin ? (
          <Chip
            icon={<ManageAccountsOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Admin"
            size="small"
            sx={{
              backgroundColor: "rgba(252, 213, 84, 0.12)",
              color: Colors.warning,
              border: "1px solid rgba(252, 213, 84, 0.3)",
              fontSize: "0.7rem",
              fontWeight: 600,
              height: "22px",
              "& .MuiChip-icon": { color: Colors.warning },
            }}
          />
        ) : (
          <Typography sx={{ fontSize: "0.8rem", color: Colors.info }}>Standard</Typography>
        )}
      </TableCell>

      <TableCell>
        {rowUser.isActive === false ? (
          <Chip
            label="Inactive"
            size="small"
            sx={{
              backgroundColor: "rgba(238, 38, 38, 0.12)",
              color: Colors.error,
              border: "1px solid rgba(238, 38, 38, 0.3)",
              fontSize: "0.7rem",
              fontWeight: 600,
              height: "22px",
            }}
          />
        ) : (
          <Chip
            label="Active"
            size="small"
            sx={{
              backgroundColor: "rgba(0, 172, 79, 0.12)",
              color: Colors.success,
              border: "1px solid rgba(0, 172, 79, 0.3)",
              fontSize: "0.7rem",
              fontWeight: 600,
              height: "22px",
            }}
          />
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 280, maxWidth: 420 }}>
        {rowUserIsAdmin ? (
          <Tooltip title="Admins have access to all organizations" placement="top" arrow>
            <Typography sx={{ fontSize: "0.8rem", color: Colors.info, cursor: "help" }}>All organizations</Typography>
          </Tooltip>
        ) : (
          <Box sx={{ maxWidth: 400 }}>
            <AutoCompleteCheckbox
              label=""
              placeholder={selectedOrganizationIds.length === 0 ? "Select organizations" : ""}
              options={orgOptions}
              value={selectedOrgOptions}
              setValue={handleOrganizationChange}
              size="small"
            />
          </Box>
        )}
      </TableCell>

      <TableCell sx={{ pr: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
          {((userIsAdmin && !rowUserIsAdmin && rowUser.isActive !== false) || process.env.REACT_APP_ENV === "development") && (
            <Tooltip title="Disable user">
              <IconButton size="small" onClick={() => onDisable(rowUser)} sx={{ color: Colors.info, "&:hover": { color: Colors.error } }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  organizations: Organization[];
  token: string;
  onSuccess: (message: string) => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose, organizations, token, onSuccess }) => {
  const [firstName, setFirstName] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>(generateRandomPassword());
  const [organizationId, setOrganizationId] = React.useState<string>("");
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [showPassword, setShowPassword] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [copied, setCopied] = React.useState<boolean>(false);

  const { showMessage } = useSnackbar();

  function resetForm() {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword(generateRandomPassword());
    setOrganizationId("");
    setIsAdmin(false);
    setError("");
    setCopied(false);
  }

  function handleClose() {
    if (loading) return;
    resetForm();
    onClose();
  }

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy password:", err);
    }
  }

  async function handleCreateUser() {
    if (!firstName || !lastName || !email || !password || !organizationId) {
      setError("All fields are required.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/admin/create`,
        { firstName, lastName, email, password, organizationId, isAdmin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showMessage("User created successfully.", "success");
      onSuccess("User created successfully");
      resetForm();
      onClose();
    } catch (err) {
      setError("Failed to create user.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: Colors.bg2,
          color: "#FFF",
          borderRadius: "12px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5, pt: 3 }}>Create User</DialogTitle>
      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
        <Typography variant="body2" sx={{ color: Colors.info, mb: 2.5 }}>
          Add a new user to your organization. They'll sign in with the temporary password you set below.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} size="small" sx={inputSx} />
            <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} size="small" sx={inputSx} />
          </Box>

          <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} size="small" fullWidth sx={inputSx} />

          <AutoComplete
            label="Location"
            placeholder="Select a location"
            options={organizations.map((org) => ({ label: org.name, value: org.organizationId }))}
            value={organizations.map((org) => ({ label: org.name, value: org.organizationId })).find((o) => o.value === organizationId) || null}
            setValue={(next) => setOrganizationId(next?.value || "")}
          />

          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: Colors.info, mb: 1, textTransform: "uppercase", letterSpacing: "0.5px" }}>Temporary password</Typography>
            <TextField
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              fullWidth
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied!" : "Copy password"}>
                      <IconButton onClick={copyPassword} edge="end" size="small" sx={{ color: copied ? Colors.success : Colors.info, mr: 0.25 }}>
                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate new password">
                      <IconButton
                        onClick={() => {
                          setPassword(generateRandomPassword());
                          setCopied(false);
                        }}
                        edge="end"
                        size="small"
                        sx={{ color: Colors.info, mr: 0.25 }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" size="small" sx={{ color: Colors.info }}>
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Typography sx={{ fontSize: "0.7rem", color: Colors.info, mt: 0.75 }}>Must be at least 8 characters with uppercase, lowercase, number, and symbol.</Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: "rgba(252, 213, 84, 0.08)",
              border: "1px solid rgba(252, 213, 84, 0.25)",
            }}
          >
            <WarningAmberIcon sx={{ color: Colors.warning, fontSize: 18, mt: 0.125, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: Colors.warning, mb: 0.25 }}>Save this password before closing</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.75)", lineHeight: 1.5 }}>
                The user will need it to sign in for the first time. It won't be shown again — copy it now and share it with them securely.
              </Typography>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                sx={{
                  color: Colors.info,
                  "&.Mui-checked": { color: Colors.primary },
                }}
              />
            }
            label={<Typography sx={{ fontSize: "0.875rem", color: "#FFF" }}>Grant admin privileges</Typography>}
            sx={{ ml: -1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ textTransform: "none", color: Colors.info }}>
          Cancel
        </Button>
        <Button onClick={handleCreateUser} variant="contained" disabled={loading} sx={containedButtonSx}>
          {loading ? "Creating..." : "Create user"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AdminPage: React.FC = () => {
  const user: FrontendUser | null = useSelector((state: RootState) => state.auth.user);

  const [users, setUsers] = React.useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = React.useState<boolean>(false);
  const [pageError, setPageError] = React.useState<string>("");
  const [query, setQuery] = React.useState<string>("");
  const [createUserModalOpen, setCreateUserModalOpen] = React.useState<boolean>(false);
  const [disableTarget, setDisableTarget] = React.useState<User | null>(null);
  const [disabling, setDisabling] = React.useState<boolean>(false);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const organizations: Organization[] = user?.organizations || [];
  const token = user?.token || "";

  function simplifyString(str: string) {
    return str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  }

  function filterUsers(allUsers: User[], q: string) {
    if (!q) return allUsers;
    return allUsers.filter((u) => {
      const userString = simplifyString(`${u.firstName} ${u.lastName} ${u.email}`);
      const simpleQuery = simplifyString(q);
      return userString.includes(simpleQuery);
    });
  }

  const filteredUsers = filterUsers(users, query);
  const adminCount = users.filter((u) => u.superOrgIds?.includes(u.superOrganizationId || "") || false).length;
  const activeCount = users.filter((u) => u.isActive !== false).length;

  React.useEffect(() => {
    const fetchUsers = async (superOrganizationId: string) => {
      try {
        setLoadingUsers(true);
        setPageError("");
        if (!token) throw new Error("No token associated to user.");

        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/super-organization/${superOrganizationId}`, { headers: { Authorization: `Bearer ${token}` } });

        setUsers(response.data.users.sort((a: User, b: User) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1)));
      } catch (error) {
        console.error("Error fetching users:", error);
        setPageError("Failed to load users.");
      } finally {
        setLoadingUsers(false);
      }
    };

    if (user?.superOrganizationId && user?.superOrgIds?.includes(user.superOrganizationId)) {
      fetchUsers(user.superOrganizationId);
    }
  }, [user?.superOrganizationId, token]);

  async function confirmDisable() {
    if (!disableTarget || !token) return;
    setDisabling(true);
    setPageError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/users/admin/disable/${disableTarget.userId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((prev) => prev.map((u) => (u.userId === disableTarget.userId ? { ...u, isActive: false } : u)).sort((a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1)));
      setSnackbar({ open: true, message: "User disabled" });
      setDisableTarget(null);
    } catch (err) {
      setPageError("Failed to disable user. Please try again.");
    } finally {
      setDisabling(false);
    }
  }

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: "#FFF",
        minHeight: "100vh",
        pb: 4,
      }}
    >
      <SignoutHeader />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
          Admin
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          Manage users, organization assignments, and admin privileges across your super organization.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ color: Colors.info }}>
          {users.length} user{users.length === 1 ? "" : "s"}
          <Box component="span" sx={{ mx: 1, opacity: 0.5 }}>
            ·
          </Box>
          {activeCount} active
          <Box component="span" sx={{ mx: 1, opacity: 0.5 }}>
            ·
          </Box>
          {adminCount} admin{adminCount === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TextField
            placeholder="Search users"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            sx={{
              ...inputSx,
              width: 280,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: Colors.info }} />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateUserModalOpen(true)} sx={containedButtonSx}>
            Add User
          </Button>
        </Box>
      </Box>

      {pageError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pageError}
        </Alert>
      )}

      {loadingUsers && users.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingUsers && users.length === 0 && (
        <Box sx={{ ...cardSx, p: 4 }}>
          <Box sx={{ textAlign: "center", py: 4, color: Colors.info }}>
            <GroupIcon sx={{ fontSize: "48px", opacity: 0.2, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 600, color: "#FFF" }}>
              No users yet
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.813rem", mb: 3 }}>
              Add your first user to get started.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateUserModalOpen(true)} sx={containedButtonSx}>
              Add User
            </Button>
          </Box>
        </Box>
      )}

      {!loadingUsers && users.length > 0 && filteredUsers.length === 0 && (
        <Box sx={{ ...cardSx, p: 4 }}>
          <Box sx={{ textAlign: "center", py: 4, color: Colors.info }}>
            <SearchIcon sx={{ fontSize: "48px", opacity: 0.2, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 600, color: "#FFF" }}>
              No matches
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.813rem" }}>
              No users match "{query}". Try a different search.
            </Typography>
          </Box>
        </Box>
      )}

      {filteredUsers.length > 0 && (
        <TableContainer component={Paper} sx={tableContainerSx}>
          <Table>
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={{ pl: 3 }}>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Organizations</TableCell>
                <TableCell sx={{ textAlign: "right", pr: 3 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((rowUser) => (
                <UserRow
                  key={rowUser.userId}
                  token={token}
                  rowUser={rowUser}
                  organizations={organizations}
                  userIsAdmin={user?.superOrgIds?.includes(user.superOrganizationId || "") || false}
                  rowUserIsAdmin={rowUser.superOrgIds?.includes(rowUser.superOrganizationId || "") || false}
                  onDisable={(u) => setDisableTarget(u)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateUserDialog
        open={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
        organizations={organizations}
        token={token}
        onSuccess={(message) => setSnackbar({ open: true, message })}
      />

      <Dialog
        open={Boolean(disableTarget)}
        onClose={() => (disabling ? undefined : setDisableTarget(null))}
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg2,
            color: "#FFF",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Disable User</DialogTitle>
        <DialogContent sx={{ color: Colors.info }}>
          Are you sure you want to disable {disableTarget?.firstName} {disableTarget?.lastName}? They will no longer be able to sign in. This action can be reversed later.
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDisableTarget(null)} disabled={disabling} sx={{ textTransform: "none", color: Colors.info }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDisable}
            variant="contained"
            color="error"
            disabled={disabling}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {disabling ? "Disabling..." : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ open: false, message: "" })} message={snackbar.message} />
    </Box>
  );
};

export default AdminPage;
